import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ElementRef,
   EventEmitter,
   Input,
   Output,
   ViewChild,
} from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Providers } from 'generated/graphql-types';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { getProviders, providerSearch } from 'src/app/common-components/helpers/provider-helper';
import { AddPhoneNumberData } from '../models/add-phone-number-data';
import * as addPhoneNumber from '../state/add-phone-number.actions';
import { getMostRecentAccount } from '../state/add-phone-number.selectors';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-add-phone-number-view',
    styleUrls: ['./add-phone-number-view.component.scss'],
    templateUrl: './add-phone-number-view.component.html',
    standalone: false
})
export class AddPhoneNumberViewComponent {
   @ViewChild('phoneNumber') phoneNumber: ElementRef;

   @Input() callState: CallState;

   @Output() formSubmitted = new EventEmitter<AddPhoneNumberData>();

   @Output() saveAndAddAnother = new EventEmitter<AddPhoneNumberData>();

   @Output() formCanceled = new EventEmitter<void>();

   @Input() form: UntypedFormGroup;

   @Input() continuousAdd: boolean;

   providers = getProviders();

   private destroyed$ = new Subject<void>();

   get selectedAccountId(): number | null {
      return this.form.get('account')?.value?.accountId ?? null;
   }

   mostRecentAccount$: Observable<{ accountId: number | null; name: string | null } | null>;

   constructor(
      private actions$: Actions,
      private changeDetectorRef: ChangeDetectorRef,
      private store: Store<AppState>
   ) {}

   ngOnInit() {
      this.mostRecentAccount$ = this.store.pipe(select(getMostRecentAccount));

      this.actions$.pipe(ofType(addPhoneNumber.createPhoneNumberAndAddAnotherSuccess)).subscribe(() => {
         this.clearPhoneNumberFields();
         this.form.markAsPristine();
         this.form.markAsUntouched();
         this.phoneNumber.nativeElement.focus();

         this.mostRecentAccount$.pipe(take(1)).subscribe((mostRecentAccount) => {
            if (mostRecentAccount) {
               this.form.get('account')?.setValue({
                  accountId: mostRecentAccount.accountId,
                  name: mostRecentAccount.name,
               });
            }
         });
      });

      const accountControl = this.form.get('account');

      accountControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((account) => {
         if (account) {
            this.form.get('account')?.setValue({ ...account }, { emitEvent: false });
         }

         this.form.get('openExtension')?.setValue(null);
         this.form.get('closedExtension')?.setValue(null);

         this.changeDetectorRef.markForCheck();
      });

      const datePortedControl = this.form.get('datePorted');
      const isPortedControl = this.form.get('isPorted');

      datePortedControl?.disable();

      isPortedControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((newValue) => {
         if (newValue === false) {
            datePortedControl?.disable();
            datePortedControl?.setValidators(null);
            datePortedControl?.setValue(null);
         } else {
            datePortedControl?.enable();
            if (!datePortedControl?.value) {
               datePortedControl?.setValue(new Date());
            }
            datePortedControl?.setValidators(Validators.required);
         }
         datePortedControl?.markAsDirty();
         datePortedControl?.updateValueAndValidity();
      });
   }

   clearPhoneNumberFields() {
      this.form.get('phoneNumber')?.setValue('');
      this.form.get('providerId')?.setValue('');
      this.form.get('isPorted')?.setValue(false);
      this.form.get('isActive')?.setValue(false);
      this.form.get('datePorted')?.setValue(null);
      this.form.get('account')?.setValue(null);
      this.changeDetectorRef.markForCheck();
   }

   onCloseClick(): void {
      this.formCanceled.emit();
   }

   onCreate(): void {
      if (this.form.valid) {
         this.formSubmitted.emit();
      }
   }

   onCreateAndAddAnother() {
      if (this.form.valid) {
         this.saveAndAddAnother.emit();
      } else {
         this.form.markAllAsTouched();
      }
   }

   providerDisplayWith(option: Providers): string {
      return `${option ?? ''}`;
   }

   providerSearch(searchText: string) {
      this.providers = providerSearch(searchText);
   }

   providerReset() {
      this.providers = getProviders();
   }
}
